/**
 * Playback telemetry: the annotation shapes the engine attaches to its log
 * records.
 *
 * The record shapes are contracts — `conversion_id`, `boundary_gap_ms`,
 * `lookahead_depth` and friends are read by dashboards — so the builders below
 * own their key names and nothing else may invent them.
 *
 * Records themselves are plain `Effect.log*` values annotated with these, run
 * on the app runtime the provider already holds (`useAppRuntime`), so they
 * carry the emitting fiber's annotations and land in whatever logger the app
 * installed: `Logger.layer` on web, the RN console + OTLP logger on mobile.
 */
export type PlaybackChunkLogContext = {
  audioId: string;
  chapterId: string;
  ttsId: string;
  playbackMode: 'legacy' | 'doc-pack';
  docPackChunkId?: string;
};

type PlaybackTtsFetchLogInput = PlaybackChunkLogContext & {
  latencyMs?: number;
  cacheHit?: boolean;
  resolved?: boolean;
};

export function playbackTtsFetchLogMetadata({
  audioId,
  chapterId,
  ttsId,
  playbackMode,
  docPackChunkId,
  latencyMs,
  cacheHit,
  resolved,
}: PlaybackTtsFetchLogInput) {
  return {
    conversion_id: audioId,
    chapter_id: chapterId,
    tts_id: ttsId,
    playback_mode: playbackMode,
    ...(docPackChunkId ? { doc_pack_chunk_id: docPackChunkId } : {}),
    ...(latencyMs !== undefined ? { latency_ms: latencyMs } : {}),
    ...(cacheHit !== undefined ? { cache_hit: cacheHit } : {}),
    ...(resolved !== undefined ? { resolved } : {}),
  };
}

export function playbackTtsFetchElapsedMs(startedAtMs: number, nowMs: number) {
  return Math.max(0, Math.round(nowMs - startedAtMs));
}

type PlaybackBoundaryLogInput = {
  audioId: string;
  playbackMode: 'legacy' | 'doc-pack';
  ttsId: string;
  docPackChunkId?: string;
  gapMs: number;
  stagesMs?: {
    resolve: number | undefined;
    load: number | undefined;
    dispatch: number | undefined;
    findNext: number | undefined;
    select: number | undefined;
    loadPrep: number | undefined;
  };
  lookaheadDepth?: number;
};

const stageField = (key: string, ms: number | undefined) =>
  ms === undefined ? {} : { [key]: Math.max(0, Math.round(ms)) };

/** One event per natural chunk transition: `boundary_gap_ms` is the
 * user-perceived silence from one chunk's onEnded to the next chunk's
 * onCanPlay (silent-retry recovery included), split into `resolve_ms` (onEnded
 * until the player is asked to load the next chunk) and `load_ms` (that load
 * until onCanPlay). `resolve_ms` is itself `dispatch_ms` (until the advance
 * runs), `find_next_ms`, `select_ms` (the store update and its synchronous
 * subscribers) and `load_prep_ms`; `lookahead_depth` is how many upcoming
 * chunks were already cached, counted once the next chunk plays — the direct
 * check on the rejection-gate prefetch design (decision §4 in
 * docs/projects/active/pausing-audio-chunk-playing-refactor/playback-architecture-decisions.md). */
export function playbackBoundaryLogMetadata({
  audioId,
  playbackMode,
  ttsId,
  docPackChunkId,
  gapMs,
  stagesMs,
  lookaheadDepth,
}: PlaybackBoundaryLogInput) {
  return {
    conversion_id: audioId,
    playback_mode: playbackMode,
    tts_id: ttsId,
    ...(docPackChunkId ? { doc_pack_chunk_id: docPackChunkId } : {}),
    boundary_gap_ms: Math.max(0, Math.round(gapMs)),
    ...stageField('resolve_ms', stagesMs?.resolve),
    ...stageField('load_ms', stagesMs?.load),
    ...stageField('dispatch_ms', stagesMs?.dispatch),
    ...stageField('find_next_ms', stagesMs?.findNext),
    ...stageField('select_ms', stagesMs?.select),
    ...stageField('load_prep_ms', stagesMs?.loadPrep),
    ...(lookaheadDepth !== undefined
      ? { lookahead_depth: lookaheadDepth }
      : {}),
  };
}

type PlaybackRecoveryAttemptLogInput = PlaybackChunkLogContext & {
  attempt: number;
  /** Absent under the time-budgeted retry policy — recovery is bounded by
   * the fetcher's retry budget, not an attempt count. */
  maxAttempts?: number;
  reason: string;
  positionMs?: number;
};

export function playbackRecoveryAttemptLogMetadata({
  audioId,
  chapterId,
  ttsId,
  playbackMode,
  attempt,
  maxAttempts,
  reason,
  docPackChunkId,
  positionMs,
}: PlaybackRecoveryAttemptLogInput) {
  return {
    conversion_id: audioId,
    chapter_id: chapterId,
    tts_id: ttsId,
    playback_mode: playbackMode,
    attempt,
    ...(maxAttempts !== undefined ? { max_attempts: maxAttempts } : {}),
    reason,
    ...(docPackChunkId ? { doc_pack_chunk_id: docPackChunkId } : {}),
    ...(positionMs !== undefined ? { position_ms: positionMs } : {}),
  };
}
